import { Trans, useLingui } from "@lingui/react/macro";
import type { DrokAssignmentRow, DrokPushAllResult } from "@rakazo/contracts";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Spinner,
  toast,
} from "@rakazo/ui-web";
import { NativeSelect, NativeSelectOption } from "@rakazo/ui-web/components/ui/native-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rakazo/ui-web/components/ui/table";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import { CheckRow } from "./check-row";

interface VersionOption {
  id: string;
  version: number;
}

interface AssignmentsPanelProps {
  templateId: string;
  archived: boolean;
  versions: VersionOption[];
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "";
}

/** Pick users who have signed in and are not blocked, then assign. */
function AssignUsers({ templateId, assigned }: { templateId: string; assigned: Set<string> }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const users = useInfiniteQuery({
    queryKey: ["drok", "admin.assignable"],
    queryFn: ({ pageParam }) => drokRpc.users.list({ cursor: pageParam, limit: 100 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const candidates = (users.data?.pages.flatMap((page) => page.users) ?? []).filter(
    (user) => user.hasPersonalSpace && !user.blocked && !assigned.has(user.id),
  );
  const assign = useMutation({
    mutationFn: () => drokRpc.assignments.assign({ templateId, userIds: picked }),
    onSuccess: async (results) => {
      const failed = results.filter((row) => row.status !== "pushed");
      if (failed.length === 0) toast.success(t`Assigned to ${results.length} people`);
      else toast.error(failed.map((row) => row.message ?? row.status).join(" "));
      setPicked([]);
      await queryClient.invalidateQueries({ queryKey: ["drok"] });
    },
    onError: (e) => setError(errorText(e)),
  });
  return (
    <div className="space-y-2" data-testid="assign-users">
      <h3 className="text-sm font-medium">
        <Trans>Assign to people</Trans>
      </h3>
      {users.isPending ? <Spinner aria-label={t`Loading…`} /> : null}
      {users.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(users.error)}
        </p>
      ) : null}
      {users.isSuccess && candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <Trans>Everyone who has signed in already has this template.</Trans>
        </p>
      ) : null}
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {candidates.map((user) => (
          <CheckRow
            key={user.id}
            checked={picked.includes(user.id)}
            onChange={(on) =>
              setPicked(on ? [...picked, user.id] : picked.filter((id) => id !== user.id))
            }
          >
            <span>{user.name}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </CheckRow>
        ))}
      </div>
      {users.hasNextPage ? (
        <Button size="sm" variant="outline" onClick={() => void users.fetchNextPage()}>
          <Trans>Load more</Trans>
        </Button>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button
        disabled={picked.length === 0 || assign.isPending}
        onClick={() => {
          setError(null);
          assign.mutate();
        }}
      >
        {assign.isPending ? <Spinner aria-label={t`Working…`} /> : null}
        <Trans>Assign</Trans>
      </Button>
    </div>
  );
}

function PushResults({ results }: { results: DrokPushAllResult[] }) {
  const names = useQuery({
    queryKey: ["drok", "admin.pushNames"],
    queryFn: () => drokRpc.users.list({ limit: 100 }),
  });
  const label = (id: string) => names.data?.users.find((user) => user.id === id)?.email ?? id;
  return (
    <ul className="space-y-1 rounded-xl bg-muted/50 p-3 text-sm" data-testid="push-results">
      {results.map((row) => (
        <li key={row.assignmentId}>
          {label(row.userId)}:{" "}
          {row.ok ? (
            <span>
              <Trans>pushed ({row.push?.outcome})</Trans>
            </span>
          ) : (
            <span className="text-destructive">{row.error}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Who has the template, which version their copy is on, and the push, unassign and re-assign controls. */
export function AssignmentsPanel({ templateId, archived, versions }: AssignmentsPanelProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const latest = versions[0]?.id ?? "";
  const [versionId, setVersionId] = useState("");
  const chosen = versionId || latest;
  const [confirm, setConfirm] = useState<DrokAssignmentRow | null>(null);
  const [results, setResults] = useState<DrokPushAllResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ["drok", "admin.assignments", templateId],
    queryFn: () => drokRpc.assignments.list({ templateId }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["drok"] });
  const fail = (e: unknown) => {
    setError(errorText(e));
    toast.error(errorText(e));
  };
  const push = useMutation({
    mutationFn: (row: DrokAssignmentRow) =>
      drokRpc.assignments.push({ assignmentId: row.id, versionId: chosen || undefined }),
    onSuccess: async (out) => {
      toast.success(t`Pushed version ${out.version}`);
      setError(null);
      await refresh();
    },
    onError: fail,
  });
  const pushAll = useMutation({
    mutationFn: () => drokRpc.assignments.pushAll({ templateId, versionId: chosen || undefined }),
    onSuccess: async (out) => {
      setResults(out);
      setError(null);
      await refresh();
    },
    onError: fail,
  });
  const unassign = useMutation({
    mutationFn: (row: DrokAssignmentRow) => drokRpc.assignments.unassign({ assignmentId: row.id }),
    onSuccess: async () => {
      toast.success(t`Unassigned`);
      setConfirm(null);
      setError(null);
      await refresh();
    },
    onError: fail,
  });
  const reassign = useMutation({
    mutationFn: (row: DrokAssignmentRow) =>
      drokRpc.assignments.assign({ templateId, userIds: [row.userId] }),
    onSuccess: async (out) => {
      const bad = out.find((row) => row.status !== "pushed");
      if (bad) fail(new Error(bad.message ?? bad.status));
      else {
        toast.success(t`Assigned again`);
        setError(null);
      }
      await refresh();
    },
    onError: fail,
  });
  const rows = list.data ?? [];
  const active = rows.filter((row) => row.state === "active");
  const busy = push.isPending || pushAll.isPending || unassign.isPending || reassign.isPending;
  return (
    <section className="space-y-4" data-testid="assignments-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">
          <Trans>Assignments</Trans>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <NativeSelect
            aria-label={t`Version to push`}
            value={chosen}
            onChange={(e) => setVersionId(e.target.value)}
          >
            {versions.map((version) => (
              <NativeSelectOption key={version.id} value={version.id}>
                {t`Version ${version.version}`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <Button
            variant="outline"
            disabled={busy || active.length === 0}
            onClick={() => {
              setError(null);
              setResults(null);
              pushAll.mutate();
            }}
          >
            {pushAll.isPending ? <Spinner aria-label={t`Working…`} /> : null}
            <Trans>Push to all</Trans>
          </Button>
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive" data-testid="assignments-error">
          {error}
        </p>
      ) : null}
      {results ? <PushResults results={results} /> : null}
      {list.isPending ? (
        <div className="grid place-items-center p-6">
          <Spinner aria-label={t`Loading…`} />
        </div>
      ) : list.isError ? (
        <div role="alert" className="rounded-xl bg-muted/50 p-4 text-sm">
          <p className="text-destructive">{errorText(list.error)}</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void list.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
          <Trans>Nobody has this template yet</Trans>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Trans>User</Trans>
                </TableHead>
                <TableHead>
                  <Trans>State</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Copy</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Last push</Trans>
                </TableHead>
                <TableHead className="text-end" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-testid="assignment-row">
                  <TableCell>
                    <p className="font-medium">{row.name}</p>
                    <p className="text-xs text-muted-foreground">{row.email}</p>
                  </TableCell>
                  <TableCell>
                    {row.state === "active" ? (
                      <Badge>
                        <Trans>Active</Trans>
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        <Trans>Unassigned</Trans>
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{row.version === null ? "" : `v${row.version}`}</TableCell>
                  <TableCell>
                    <p className="whitespace-nowrap text-xs">{when(row.lastPushedAt)}</p>
                    {row.lastPushError ? (
                      <p className="text-xs text-destructive">{row.lastPushError}</p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-1">
                      {row.state === "active" ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => push.mutate(row)}
                          >
                            <Trans>Push</Trans>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => setConfirm(row)}
                          >
                            <Trans>Unassign</Trans>
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy || archived}
                          onClick={() => reassign.mutate(row)}
                        >
                          <Trans>Assign again</Trans>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {archived ? null : (
        <AssignUsers templateId={templateId} assigned={new Set(active.map((row) => row.userId))} />
      )}
      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => (open ? undefined : setConfirm(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Unassign {confirm?.email}?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Their copy is archived and stops running. You can assign it again later.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unassign.isPending}>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={unassign.isPending}
              onClick={() => confirm && unassign.mutate(confirm)}
            >
              {unassign.isPending ? <Spinner aria-label={t`Working…`} /> : null}
              <Trans>Unassign</Trans>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
